import { I18n } from "@assets/strings";
import { Set } from "immutable";
import React, { useEffect, useState } from "react";
import { ClipLoader } from "react-spinners";
import styled from "styled-components";
import { useServices } from "@core/servicesContext";
import { Group } from "@domain/group/group";
import { useCourseWorkspaces } from "@domain/group/workspace/hooks";
import { LightCourse } from "@domain/lms/course/course";
import { useAsync } from "@utils/hooks";
import { ClosableModal } from "@ui/modals/closableModal";
import { gap } from "@ui/styles/utils";
import { createToast } from "@ui/toasts/utils";
import { LoadingButton } from "@ui/components/buttons/loadingButton";
import { PrimaryMedium } from "@ui/components/containers/primary";
import { Secondary } from "@ui/components/containers/textBlocks";
import { ChevronDownIcon } from "@ui/components/icons";
import { RadioButton } from "@ui/components/inputs/radio";
import { row, Stack } from "@ui/components/layout";

interface CourseShareroomsModalProps extends React.HTMLAttributes<HTMLDivElement> {
	course: LightCourse;
	sharerooms: Group[];
	close: () => void;
}
export const CourseShareroomsModal: React.FC<CourseShareroomsModalProps> = ({
	course,
	sharerooms,
	close,
	...htmlProps
}) => {
	const { workspaceService } = useServices();
	const { result: workspaces, loading: workspacesLoading } = useCourseWorkspaces(course.id);
	const [workspaceIds, setWorkspaceIds] = useState<Set<string>>(Set());

	useEffect(() => {
		if (!workspacesLoading && workspaces) {
			setWorkspaceIds(Set(workspaces.map(({ id }) => id)));
		}
	}, [workspacesLoading]);

	const { loading, run } = useAsync(async () => {
		const alreadyUsedWorkspaces = Set(workspaces.map(({ id }) => id));
		try {
			await workspaceService.addCourseToWorkspaces(
				course.id,
				workspaceIds.filter((id) => !alreadyUsedWorkspaces.has(id)).toArray()
			);
			await workspaceService.removeCourseFromWorkspaces(
				course.id,
				alreadyUsedWorkspaces.filter((id) => !workspaceIds.has(id)).toArray()
			);
			createToast(I18n.t("success"), I18n.t("course.workspaces.success"), "success");
			close();
		} catch {
			createToast(I18n.t("error"), I18n.t("course.workspaces.error"), "error");
		}
	}, [workspaceIds, workspaces]);

	return (
		<Container title={I18n.t("course.workspaces.add")} close={close} {...htmlProps}>
			{workspacesLoading ? (
				<ClipLoader />
			) : (
				<>
					<Content>
						{sharerooms &&
							sharerooms.map((room) => (
								<FoldableRoom
									initialyOpen={sharerooms.length < 2}
									key={room.id}
									shareroom={room}
									workspaceIds={workspaceIds}
									onWorkspaceToggled={(workspaceId) =>
										setWorkspaceIds(
											workspaceIds.update((ids) =>
												ids.has(workspaceId) ? ids.remove(workspaceId) : ids.add(workspaceId)
											)
										)
									}
								/>
							))}
					</Content>
					<LoadingButton onClick={run} loading={loading}>
						{I18n.t("validate")}
					</LoadingButton>
				</>
			)}
		</Container>
	);
};

const Container = styled(ClosableModal)`
	${gap(40)};

	> button {
		margin: 0 auto;
	}
`;

const Content = styled.div`
	${gap(20)};

	> :not(:last-child) {
		border-bottom: 1px solid ${({ theme }) => theme.colors.lightergray};
	}
`;

interface FoldableRoomProps extends React.HTMLAttributes<HTMLDivElement> {
	shareroom: Group;
	workspaceIds: Set<string>;
	initialyOpen?: boolean;
	onWorkspaceToggled: (id: string) => void;
}

export const FoldableRoom: React.FC<FoldableRoomProps> = ({
	shareroom,
	workspaceIds,
	onWorkspaceToggled,
	initialyOpen = false,
	...htmlProps
}) => {
	const [opened, setOpened] = useState(initialyOpen);

	return (
		<RoomContainer {...htmlProps} onClick={() => !opened && setOpened(true)}>
			<ShareroomTitle onClick={() => setOpened(!opened)} opened={opened}>
				<PrimaryMedium>{shareroom.name}</PrimaryMedium>
				<ChevronDownIcon />
			</ShareroomTitle>
			{opened ? (
				<Stack gap={10}>
					{shareroom &&
						shareroom.workspaces &&
						shareroom.workspaces?.map((workspace) => (
							<RadioButton
								key={workspace.id}
								id={workspace.id}
								value={workspace.id}
								checkbox
								name={shareroom.id}
								label={workspace.isDefault ? I18n.t("workspace.name.default") : workspace.name}
								checked={workspaceIds.has(workspace.id)}
								onChange={(v) => onWorkspaceToggled(v)}
							/>
						))}
				</Stack>
			) : (
				<Secondary>{I18n.t("course.workspaces.count", { count: shareroom.workspaces?.length ?? 0 })}</Secondary>
			)}
		</RoomContainer>
	);
};

const RoomContainer = styled.div`
	padding: 20px 0;
`;

const ShareroomTitle = styled.div.withConfig({
	shouldForwardProp: (prop) => prop !== "opened",
})<{ opened: boolean }>`
	cursor: pointer;
	${row(10, "space-between", "center")};
	margin-bottom: ${({ opened }) => (opened ? 20 : 4)}px;

	svg {
		flex-shrink: 0;
		${({ opened }) => opened && "transform: rotate(180deg)"};
	}

	div {
		color: ${({ theme, opened }) => (opened ? theme.colors.secondary : theme.colors.textPrimary)};
	}
`;
